import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PageHeader from '../components/PageHeader';

const API_URL = import.meta.env.VITE_API_URL || '';

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const syncSubscription = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) return;

        // Sync subscription state after checkout
        await axios.post(
          `${API_URL}/api/subscriptions/sync`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Error syncing subscription after checkout:', error);
      }
    };

    syncSubscription();

    // Redirect to dashboard after 5 seconds (replace to avoid back loop)
    const timer = setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, 5000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader title="Plată Reușită!" subtitle="Bine ai venit în noul tău plan" backTo="/dashboard" />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          {/* Success Icon */}
          <div className="mb-6">
            <div className="mx-auto w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <svg
                className="w-12 h-12 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          {/* Success Message */}
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            🎉 Upgrade Reușit!
          </h2>
          
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            Abonamentul tău a fost activat cu succes. Creditele tale au fost adăugate și poți începe să folosești toate funcționalitățile AI imediat!
          </p>

          {sessionId && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                ID Sesiune: <span className="font-mono">{sessionId.substring(0, 20)}...</span>
              </p>
            </div>
          )}

          {/* Benefits */}
          <div className="mb-8 space-y-3 text-left max-w-md mx-auto">
            <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Creditele tale au fost adăugate</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Acces complet la toate funcționalitățile AI</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Email de confirmare trimis</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition"
            >
              Mergi la Dashboard
            </button>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            >
              Continuă
            </button>
          </div>

          <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
            Vei fi redirecționat automat la Dashboard în 5 secunde...
          </p>
        </div>

        {/* What's Next */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
            🚀 Ce urmează?
          </h3>
          <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
            <li>✅ Explorează chat-ul AI cu nutritionistul virtual</li>
            <li>✅ Generează planuri de mese personalizate</li>
            <li>✅ Analizează poze cu alimente pentru tracking automat</li>
            <li>✅ Obține exerciții personalizate pentru obiectivele tale</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
